/**
 * Alaska State-Specific Diagnostic Questions
 * 
 * REGULATORY FRAMEWORK:
 * Alaska REQUIRES state licensure for home health agencies under AS 47.32.010-900
 * 
 * Applicable Regulations:
 * - Home Health Agencies: 7 AAC 12.500 - 7 AAC 12.590
 * - General Provisions: 7 AAC 12.600 - 7 AAC 12.990
 * - Background Checks: 7 AAC 10.900 - 7 AAC 10.990, AS 47.05.310
 * - Licensing Statute: AS 47.32.010 - 47.32.900
 * 
 * Key Sections:
 * - 7 AAC 12.500: Home Health Agency Definition
 * - 7 AAC 12.510: Administration
 * - 7 AAC 12.512: Quality Improvement
 * - 7 AAC 12.513: Plan of Care
 * - 7 AAC 12.516: Director of Clinical Services
 * - 7 AAC 12.517: Nursing Services
 * - 7 AAC 12.519: Home Health Aide Services
 * - 7 AAC 12.531: Agency Policies and Procedures
 * - 7 AAC 12.534: Patients' Rights
 * - 7 AAC 12.541: Abuse, Neglect, Mistreatment
 * 
 * License Renewal: Biennial (every 2 years) - due April 1 of even-numbered years
 * Regulatory Body: Alaska Department of Health (formerly DHSS)
 */

import type { DiagnosticQuestionConfig } from '../../../types/diagnostic';

export const ALASKA_QUESTIONS: DiagnosticQuestionConfig[] = [
  // ============================================
  // SECTION A: LICENSING & OPERATIONS
  // ============================================
  {
    id: 'AK-A1',
    itemId: 'A1',
    stateCode: 'AK',
    title: 'Home Health Agency License Status',
    description: 'Alaska requires all home health agencies to be licensed under AS 47.32 and 7 AAC 12.500-590',
    severity: 'high',
    regulationCode: 'AS 47.32.010',
    regulationName: 'License',
    agencyName: 'Alaska Department of Health - Health Facility Licensing and Certification',
    agencyAbbr: 'DOH-HFLC',
    agencyPhone: '(907) 334-2483',
    agencyUrl: 'https://health.alaska.gov/en/services/health-care-facilities-licensing-certification-all/home-health-agencies-hhas/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a current, active Home Health Agency license from the Alaska Department of Health?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Home health agency licenses must be renewed biennially (every 2 years) by April 1 of even-numbered years.',
      },
      {
        id: 'q2',
        question: 'What is your license expiration date?',
        inputType: 'date',
        helpText: 'Found on your home health agency license certificate. Next renewal deadline: April 1, 2026.',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 90, result: 'COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 45, result: 'AT_RISK' },
      { condition: 'daysFuture', questionId: 'q2', value: 0, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your Alaska Home Health Agency license is current and active per AS 47.32.',
      atRisk: 'Your license expires within 90 days. Submit renewal application to Health Facility Licensing and Certification before April 1 of even-numbered years.',
      nonCompliant: 'Operating a home health agency in Alaska without a valid license violates AS 47.32 and can result in civil penalties up to $500/day. Contact HFLC immediately at (907) 334-2483.',
      unknown: 'Verify your home health agency license status with HFLC at (907) 334-2483 or doh.hflc.info@alaska.gov.',
    },
    stateNotes: [
      'Home health agency licenses are valid for 2 years (biennial)',
      'Renewal due April 1 of even-numbered years',
      'Initial license requires on-site survey',
      'Provisional licensing fees: $500-$3,000 based on FTEs',
      'Alaska also requires Medicare certification for participation in federal programs',
    ],
  },

  {
    id: 'AK-A2',
    itemId: 'A2',
    stateCode: 'AK',
    title: 'Administrator Qualifications (7 AAC 12.510)',
    description: 'Alaska requires qualified administrators for home health agencies',
    severity: 'high',
    regulationCode: '7 AAC 12.510',
    regulationName: 'Administrator',
    agencyName: 'Alaska Department of Health - Health Facility Licensing and Certification',
    agencyAbbr: 'DOH-HFLC',
    agencyPhone: '(907) 334-2483',
    agencyUrl: 'https://health.alaska.gov/en/services/health-care-facilities-licensing-certification-all/home-health-agencies-hhas/',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have a qualified administrator who meets Alaska requirements?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Administrator must meet education and experience requirements per 7 AAC 12.510',
      },
      {
        id: 'q2',
        question: 'Is the administrator designated in writing with documentation on file?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
      {
        id: 'q3',
        question: 'Have you submitted a Change of Leadership form to HFLC if administrator changed?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes - form submitted' },
          { value: 'na', label: 'N/A - no change in leadership' },
          { value: 'no', label: 'No - not submitted' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Administrator qualifications meet 7 AAC 12.510 requirements with documentation on file.',
      atRisk: 'Administrator designation documentation is missing or Change of Leadership form not submitted. Update files and notify HFLC of any leadership changes.',
      nonCompliant: 'Your agency lacks a qualified administrator per 7 AAC 12.510. This is a critical licensing requirement.',
      unknown: 'Verify administrator qualifications meet 7 AAC 12.510 requirements.',
    },
    stateNotes: [
      'Administrator must have education and experience per 7 AAC 12.510',
      'Change of Leadership form required within 30 days of any administrator change',
      'Administrator responsibilities include oversight of all agency operations',
      'Documentation of qualifications must be maintained in agency records',
    ],
  },

  {
    id: 'AK-A3',
    itemId: 'A3',
    stateCode: 'AK',
    title: 'Director of Clinical Services (7 AAC 12.516)',
    description: 'Alaska requires a qualified director of clinical services',
    severity: 'high',
    regulationCode: '7 AAC 12.516',
    regulationName: 'Clinical Director',
    agencyName: 'Alaska Department of Health',
    agencyAbbr: 'DOH',
    agencyPhone: '(907) 334-2483',
    agencyUrl: 'https://health.alaska.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a designated Director of Clinical Services who is a licensed physician or registered nurse?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Does the Director have current Alaska professional licensure?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes - verified current' },
          { value: 'no', label: 'No / Not verified' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Director of Clinical Services meets 7 AAC 12.516 requirements.',
      atRisk: 'Verify Director of Clinical Services has current Alaska licensure.',
      nonCompliant: 'No qualified Director of Clinical Services designated. This violates 7 AAC 12.516.',
      unknown: 'Review Director of Clinical Services requirements.',
    },
    stateNotes: [
      'Director must be a licensed physician or registered nurse per 7 AAC 12.516',
      'RN Directors must hold current Alaska nursing license from Board of Nursing',
      'Director is responsible for clinical oversight and care quality',
      'Verify license status at commerce.alaska.gov/cbpl/ProfessionalLicensing',
    ],
  },

  {
    id: 'AK-A4',
    itemId: 'A4',
    stateCode: 'AK',
    title: 'Written Policies and Procedures (7 AAC 12.531)',
    description: 'Alaska requires specific written policies per 7 AAC 12.531',
    severity: 'high',
    regulationCode: '7 AAC 12.531',
    regulationName: 'Policies',
    agencyName: 'Alaska Department of Health',
    agencyAbbr: 'DOH',
    agencyPhone: '(907) 334-2483',
    agencyUrl: 'https://health.alaska.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Which of the following Alaska-required policies does your agency have documented? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'patient_rights', label: 'Patient rights and responsibilities (7 AAC 12.534)' },
          { value: 'services', label: 'Scope of services provided' },
          { value: 'personnel', label: 'Personnel qualifications and duties' },
          { value: 'supervision', label: 'Clinical supervision procedures' },
          { value: 'emergency', label: 'Emergency preparedness and response' },
          { value: 'complaint', label: 'Grievance and complaint procedures' },
          { value: 'infection', label: 'Infection control program' },
          { value: 'discharge', label: 'Admission, discharge, and transfer' },
          { value: 'records', label: 'Medical records management' },
          { value: 'quality', label: 'Quality improvement program (7 AAC 12.512)' },
          { value: 'abuse', label: 'Abuse, neglect, mistreatment (7 AAC 12.541)' },
          { value: 'advance_dir', label: 'Advance directives (7 AAC 12.537)' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', questionId: 'q1', value: 11, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 8, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 8, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your policy manual meets Alaska 7 AAC 12.531 requirements.',
      atRisk: 'Your policy manual is missing required sections per 7 AAC 12.531. HFLC surveyors will review your policies for completeness.',
      nonCompliant: 'Multiple required policies are missing per 7 AAC 12.531. This is a common survey deficiency.',
      unknown: 'Review your policies against 7 AAC 12.531 requirements.',
    },
    stateNotes: [
      '7 AAC 12.531 specifies required policies for HHAs',
      'Policies must be reviewed annually and updated as needed',
      'Patient rights policy required per 7 AAC 12.534',
      'Abuse/neglect policy required per 7 AAC 12.541',
      'HFLC surveyors review policy manuals during licensing surveys',
    ],
  },

  {
    id: 'AK-A5',
    itemId: 'A5',
    stateCode: 'AK',
    title: 'Medicare/Medicaid Certification',
    description: 'Medicare and Alaska Medicaid certification status',
    severity: 'high',
    regulationCode: '42 CFR 484',
    regulationName: 'Medicare CoP',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Is your agency Medicare certified?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Medicare certification requires meeting federal CoP in addition to Alaska state licensure.',
      },
      {
        id: 'q2',
        question: 'Is your agency enrolled as an Alaska Medicaid provider?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your agency maintains Medicare and Alaska Medicaid certification.',
      atRisk: 'Consider obtaining Medicare/Medicaid certification to serve more patients. Alaska Medicaid covers HCBS waiver services.',
      nonCompliant: 'Missing certification limits patient base.',
      unknown: 'Verify certification status with CMS and Alaska Medicaid.',
    },
    stateNotes: [
      'Alaska state license is separate from Medicare certification',
      'Medicare-certified agencies must meet both state and federal requirements',
      'Alaska Medicaid HCBS waivers provide additional service authorization',
    ],
  },

  {
    id: 'AK-A6',
    itemId: 'A6',
    stateCode: 'AK',
    title: 'Quality Improvement Program (7 AAC 12.512)',
    description: 'Alaska requires an ongoing quality improvement program',
    severity: 'high',
    regulationCode: '7 AAC 12.512',
    regulationName: 'Quality Improvement',
    agencyName: 'Alaska Department of Health',
    agencyAbbr: 'DOH',
    agencyPhone: '(907) 334-2483',
    agencyUrl: 'https://health.alaska.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a formal quality improvement program per 7 AAC 12.512?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Which elements does your QI program include? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'policies', label: 'Written QI policies and procedures' },
          { value: 'monitoring', label: 'Ongoing monitoring of care quality' },
          { value: 'data', label: 'Data collection and analysis' },
          { value: 'projects', label: 'Performance improvement projects' },
          { value: 'documentation', label: 'Documentation of QI activities' },
          { value: 'outcomes', label: 'Patient outcome tracking' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
      {
        id: 'q3',
        question: 'How often do you conduct QI reviews?',
        inputType: 'select',
        options: [
          { value: 'monthly', label: 'Monthly' },
          { value: 'quarterly', label: 'Quarterly' },
          { value: 'annually', label: 'Annually' },
          { value: 'never', label: 'No regular reviews' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
        { condition: 'includes', questionId: 'q3', value: ['monthly', 'quarterly'] },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'never', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Quality improvement program meets 7 AAC 12.512 requirements.',
      atRisk: 'QI program needs enhancement or more frequent reviews.',
      nonCompliant: 'No QI program exists. This violates 7 AAC 12.512.',
      unknown: 'Review QI requirements per 7 AAC 12.512.',
    },
    stateNotes: [
      '7 AAC 12.512 requires ongoing quality improvement activities',
      'QI program must include data collection and outcome monitoring',
      'Performance improvement projects should address identified issues',
      'Documentation of QI activities required for survey',
    ],
  },

  {
    id: 'AK-A7',
    itemId: 'A7',
    stateCode: 'AK',
    title: 'Liability Insurance Coverage',
    description: 'Professional liability insurance requirements',
    severity: 'high',
    regulationCode: '7 AAC 12.610',
    regulationName: 'Insurance',
    agencyName: 'Alaska Department of Health',
    agencyAbbr: 'DOH',
    agencyPhone: '(907) 334-2483',
    agencyUrl: 'https://health.alaska.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you maintain professional liability (malpractice) insurance?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'What is your liability coverage amount per occurrence?',
        inputType: 'select',
        options: [
          { value: '1m_plus', label: '$1,000,000 or more' },
          { value: '500k_1m', label: '$500,000 - $999,999' },
          { value: 'under_500k', label: 'Under $500,000' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
      {
        id: 'q3',
        question: 'Do you maintain general liability insurance?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: '1m_plus' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'under_500k', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Liability insurance coverage is adequate.',
      atRisk: 'Consider increasing liability coverage to at least $1,000,000.',
      nonCompliant: 'Operating without liability insurance creates significant risk and may violate licensing requirements.',
      unknown: 'Review insurance requirements.',
    },
    stateNotes: [
      '7 AAC 12.610 addresses insurance requirements',
      'Recommended minimum: $1,000,000 per occurrence professional liability',
      'General liability insurance should also be maintained',
      'Verify coverage includes home health services and all service areas',
    ],
  },

  {
    id: 'AK-A8',
    itemId: 'A8',
    stateCode: 'AK',
    title: 'Workers Compensation Insurance',
    description: 'Alaska requires workers compensation coverage for all employers',
    severity: 'high',
    regulationCode: 'AS 23.30',
    regulationName: 'Workers Compensation',
    agencyName: 'Alaska Department of Labor and Workforce Development',
    agencyAbbr: 'DOL',
    agencyPhone: '(907) 465-2790',
    agencyUrl: 'https://labor.alaska.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have current workers compensation insurance?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Alaska requires workers compensation for ALL employers with one or more employees.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Workers compensation coverage meets Alaska requirements under AS 23.30.',
      atRisk: 'Verify workers compensation coverage status.',
      nonCompliant: 'Operating without workers compensation violates Alaska law. All Alaska employers must carry workers comp.',
      unknown: 'Verify coverage with Alaska Department of Labor.',
    },
    stateNotes: [
      'Alaska requires workers comp for ALL employers (no minimum employee threshold)',
      'Penalties for non-compliance include civil and criminal penalties',
    ],
  },

  {
    id: 'AK-A9',
    itemId: 'A9',
    stateCode: 'AK',
    title: 'Business License Registration',
    description: 'Alaska business license requirements',
    severity: 'medium',
    regulationCode: 'AS 43.70',
    regulationName: 'Business License',
    agencyName: 'Alaska Division of Corporations, Business and Professional Licensing',
    agencyAbbr: 'CBPL',
    agencyPhone: '(907) 465-2550',
    agencyUrl: 'https://www.commerce.alaska.gov/web/cbpl/',
    questions: [
      {
        id: 'q1',
        question: 'Is your business properly registered with the Alaska Division of Corporations?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No / Not sure' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you have a current Alaska Business License?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Business registration and licensing meets Alaska requirements.',
      atRisk: 'Verify business registration status.',
      nonCompliant: 'Business registration issues may affect HHA licensing.',
      unknown: 'Check status at commerce.alaska.gov.',
    },
    stateNotes: [
      'Alaska Business License required under AS 43.70',
      'Business license renewal is annual',
      'Entity must be registered with Division of Corporations',
      'Online verification available at commerce.alaska.gov/cbpl',
    ],
  },

  {
    id: 'AK-A10',
    itemId: 'A10',
    stateCode: 'AK',
    title: 'Service Area and Remote Access Plan',
    description: 'Alaska-specific considerations for remote and rural service areas',
    severity: 'medium',
    regulationCode: '7 AAC 12.510',
    regulationName: 'Service Area',
    agencyName: 'Alaska Department of Health',
    agencyAbbr: 'DOH',
    agencyPhone: '(907) 334-2483',
    agencyUrl: 'https://health.alaska.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you serve patients in remote or rural Alaska communities (e.g., bush communities, island communities)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you have documented procedures for serving remote patients?',
        inputType: 'checklist',
        options: [
          { value: 'travel', label: 'Staff travel and safety protocols' },
          { value: 'communication', label: 'Communication contingency plans' },
          { value: 'supplies', label: 'Supply and equipment logistics' },
          { value: 'emergency', label: 'Emergency evacuation procedures' },
          { value: 'weather', label: 'Weather-related contingencies' },
          { value: 'telehealth', label: 'Telehealth/remote monitoring options' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q2', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Remote service procedures are adequate for Alaska operations.',
      atRisk: 'Enhance remote service procedures for better coverage in rural Alaska.',
      nonCompliant: 'Serving remote Alaska communities requires documented procedures for safety and continuity.',
      unknown: 'Review remote service requirements.',
    },
    stateNotes: [
      'Alaska has unique challenges with weather, terrain, and accessibility',
      'Many communities are only accessible by air or water',
      'Plan for extended isolation during winter months',
    ],
  },

  // ============================================
  // SECTION B: PERSONNEL FILES
  // ============================================
  {
    id: 'AK-B1',
    itemId: 'B1',
    stateCode: 'AK',
    title: 'Personnel File Requirements (7 AAC 12.620)',
    description: 'Alaska requires specific documents in each employee personnel file',
    severity: 'high',
    regulationCode: '7 AAC 12.620',
    regulationName: 'Personnel Records',
    agencyName: 'Alaska Department of Health',
    agencyAbbr: 'DOH',
    agencyPhone: '(907) 334-2483',
    agencyUrl: 'https://health.alaska.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Which of the following documents do you maintain in EVERY employee personnel file? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'app', label: 'Employment application' },
          { value: 'credentials', label: 'Professional licenses and certifications' },
          { value: 'background', label: 'Background check clearance (BCPU)' },
          { value: 'references', label: 'Reference verification' },
          { value: 'tb', label: 'TB screening documentation' },
          { value: 'health', label: 'Health assessment/clearance' },
          { value: 'orientation', label: 'Orientation completion documentation' },
          { value: 'competency', label: 'Competency evaluations' },
          { value: 'training', label: 'Training records' },
          { value: 'job_desc', label: 'Job description acknowledgment' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', questionId: 'q1', value: 9, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 7, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 7, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Personnel files contain all Alaska-required documentation per 7 AAC 12.620.',
      atRisk: 'Some personnel files are missing required documents. HFLC surveyors will review all personnel files.',
      nonCompliant: 'Personnel files are missing critical documentation. Immediately obtain required documents.',
      unknown: 'Audit personnel files against 7 AAC 12.620 requirements.',
    },
    stateNotes: [
      '7 AAC 12.620 specifies personnel record requirements',
      'BCPU clearance documentation required before patient contact',
      'Professional license copies must be in personnel files',
      'Training records and competency evaluations must be documented',
      'HFLC surveyors review personnel files during surveys',
    ],
  },

  {
    id: 'AK-B2',
    itemId: 'B2',
    stateCode: 'AK',
    title: 'Professional License Verification',
    description: 'Alaska requires verification of all professional licenses',
    severity: 'high',
    regulationCode: '7 AAC 12.620',
    regulationName: 'License Verification',
    agencyName: 'Alaska Board of Nursing',
    agencyAbbr: 'ABON',
    agencyPhone: '(907) 465-2550',
    agencyUrl: 'https://www.commerce.alaska.gov/web/cbpl/ProfessionalLicensing/BoardofNursing.aspx',
    questions: [
      {
        id: 'q1',
        question: 'Do you verify professional licenses (RN, LPN, PT, etc.) before hiring?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always verified with Alaska Board of Nursing/Licensure' },
          { value: 'sometimes', label: 'Sometimes verified' },
          { value: 'no', label: 'No verification process' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you track license expiration dates and verify renewal?',
        inputType: 'select',
        options: [
          { value: 'system', label: 'Automated tracking system with alerts' },
          { value: 'manual', label: 'Manual tracking (spreadsheet, calendar)' },
          { value: 'none', label: 'No active tracking' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'includes', questionId: 'q2', value: ['system', 'manual'] },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'sometimes', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Professional license verification process meets Alaska requirements.',
      atRisk: 'License verification is inconsistent. Implement systematic verification.',
      nonCompliant: 'Employing unlicensed professionals violates Alaska law. Verify all professional licenses.',
      unknown: 'Review license verification processes.',
    },
    stateNotes: [
      'Verify licenses with Alaska Board of Nursing or appropriate licensing board',
      'Online verification: commerce.alaska.gov/cbpl/ProfessionalLicensing',
      'License expiration tracking is essential for compliance',
      'Employing professionals with expired licenses violates Alaska law',
    ],
  },

  {
    id: 'AK-B3',
    itemId: 'B3',
    stateCode: 'AK',
    title: 'Job Descriptions',
    description: 'Written job descriptions for all positions',
    severity: 'medium',
    regulationCode: '7 AAC 12.620',
    regulationName: 'Personnel',
    agencyName: 'Alaska Department of Health',
    agencyAbbr: 'DOH',
    agencyPhone: '(907) 334-2483',
    agencyUrl: 'https://health.alaska.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Does every employee have a written job description?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do employees sign acknowledgment of their job description?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Job descriptions complete and signed.',
      atRisk: 'Obtain signed acknowledgments.',
      nonCompliant: 'Job descriptions missing.',
      unknown: 'Review job description requirements.',
    },
    stateNotes: [
      'Job descriptions required per 7 AAC 12.620',
      'Must include qualifications, duties, and responsibilities',
      'Employee acknowledgment signature recommended',
      'Job descriptions should be reviewed and updated annually',
    ],
  },

  // ============================================
  // SECTION C: BACKGROUND SCREENING
  // ============================================
  {
    id: 'AK-C1',
    itemId: 'C1',
    stateCode: 'AK',
    title: 'Background Check Program (AS 47.05.310)',
    description: 'Alaska requires criminal background checks through the Background Check Program (BCP) per AS 47.05.300-390 and 7 AAC 10.900-990',
    severity: 'high',
    regulationCode: 'AS 47.05.310',
    regulationName: 'Background Check',
    agencyName: 'Alaska Department of Health - Background Check Program',
    agencyAbbr: 'BCP',
    agencyPhone: '(907) 334-4475',
    agencyUrl: 'https://health.alaska.gov/en/services/background-check-program/',
    questions: [
      {
        id: 'q1',
        question: 'For ALL direct care staff, which background checks do you have on file? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'bcpu', label: 'Background Check Program Unit (BCPU) clearance' },
          { value: 'state', label: 'Alaska State criminal background check' },
          { value: 'federal', label: 'FBI national criminal background check (fingerprint-based)' },
          { value: 'sex_offender', label: 'Sex Offender Registry check' },
          { value: 'oig', label: 'OIG Exclusion List verification' },
        ],
        required: true,
        helpText: 'Alaska requires BCPU clearance for all direct care staff before patient contact per AS 47.05.310 and 7 AAC 10.900-990',
      },
      {
        id: 'q2',
        question: 'Are background checks completed BEFORE employees have any patient contact?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes - always before patient contact' },
          { value: 'no', label: 'No - sometimes they start early' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      // NON_COMPLIANT if staff start before clearance (direct violation of AS 47.05.310 - no patient contact allowed)
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      // NON_COMPLIANT if missing required checks
      { condition: 'countLessThan', questionId: 'q1', value: 2, result: 'NON_COMPLIANT' },
      // COMPLIANT only if 4+ checks AND always complete before patient contact
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 4 },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      // AT_RISK if 2-3 checks but not all required
      { condition: 'countBetween', questionId: 'q1', value: [2, 3], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Background check documentation is complete for all direct care staff per AS 47.05.310 and 7 AAC 10.900-990.',
      atRisk: 'Background checks may be incomplete. Review all employee files and ensure all required BCPU checks are completed.',
      nonCompliant: 'VIOLATION: Staff are working before background clearance OR required checks are missing. Per AS 47.05.310, no patient contact is permitted until BCPU clearance is received.',
      unknown: 'Audit all employee files for background check documentation.',
    },
    stateNotes: [
      'Alaska BCPU clearance is MANDATORY for all direct care staff',
      'Barrier crimes list in 7 AAC 10.905 specifies disqualifying offenses',
      'Fingerprint-based FBI check required in addition to state check',
    ],
  },

  {
    id: 'AK-C2',
    itemId: 'C2',
    stateCode: 'AK',
    title: 'BCP Registration and Clearance (7 AAC 10.900)',
    description: 'All direct care providers must be registered and cleared through the Background Check Program',
    severity: 'high',
    regulationCode: '7 AAC 10.900',
    regulationName: 'BCP Registration',
    agencyName: 'Alaska Department of Health - Background Check Program',
    agencyAbbr: 'BCP',
    agencyPhone: '(907) 334-4475',
    agencyUrl: 'https://health.alaska.gov/en/services/background-check-program/',
    questions: [
      {
        id: 'q1',
        question: 'Are all direct care staff registered and cleared through BCPU?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff are BCPU cleared' },
          { value: 'most', label: 'Most are cleared, some pending' },
          { value: 'no', label: 'No / Not using BCPU' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you have a process to track BCPU clearance status?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All direct care staff are registered and cleared through BCPU per 7 AAC 10.900.',
      atRisk: 'Some staff pending BCPU clearance. Ensure NO patient contact until clearance is received.',
      nonCompliant: 'Direct care staff must be cleared through BCPU per 7 AAC 10.900. Contact BCPU immediately.',
      unknown: 'Verify BCPU registration and clearance status for all direct care staff.',
    },
    stateNotes: [
      'BCPU registration required under 7 AAC 10.900-990',
      'No patient contact allowed until clearance received',
      'BCPU contact: (907) 334-4475',
      'Online portal available for employer registration and tracking',
    ],
  },

  {
    id: 'AK-C3',
    itemId: 'C3',
    stateCode: 'AK',
    title: 'Barrier Crimes Policy (7 AAC 10.905)',
    description: 'Policy for handling disqualifying barrier crimes',
    severity: 'high',
    regulationCode: '7 AAC 10.905',
    regulationName: 'Barrier Crimes',
    agencyName: 'Alaska BCPU',
    agencyAbbr: 'BCPU',
    agencyPhone: '(907) 334-2483',
    agencyUrl: 'https://health.alaska.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a written policy for handling barrier crime findings per 7 AAC 10.905?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Are hiring managers trained on Alaska barrier crime requirements?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Barrier crimes policy documented and staff trained.',
      atRisk: 'Train hiring managers on barrier crime requirements.',
      nonCompliant: 'No barrier crimes policy. 7 AAC 10.905 lists disqualifying offenses.',
      unknown: 'Review barrier crime requirements.',
    },
    stateNotes: [
      '7 AAC 10.905 lists barrier crimes that disqualify individuals',
      'Permanent barriers include crimes against persons and sexual offenses',
      'Time-limited barriers may allow employment after specified period',
      'Variance process available for certain non-permanent barriers',
    ],
  },

  {
    id: 'AK-C4',
    itemId: 'C4',
    stateCode: 'AK',
    title: 'OIG Exclusion List Check',
    description: 'Federal exclusion list verification required for Medicare/Medicaid billing',
    severity: 'high',
    regulationCode: '42 CFR 1001',
    regulationName: 'OIG Exclusion',
    agencyName: 'Office of Inspector General',
    agencyAbbr: 'OIG',
    agencyPhone: '(800) 447-8477',
    agencyUrl: 'https://exclusions.oig.hhs.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you check the OIG exclusion list (LEIE) before hiring?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'How often do you re-check for current employees?',
        inputType: 'select',
        options: [
          { value: 'monthly', label: 'Monthly' },
          { value: 'quarterly', label: 'Quarterly' },
          { value: 'annually', label: 'Annually' },
          { value: 'never', label: 'Only at hire' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'monthly' },
      ], result: 'COMPLIANT' },
      { condition: 'includes', questionId: 'q2', value: ['quarterly', 'annually'], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'OIG checks completed at hire and monthly.',
      atRisk: 'OIG recommends monthly screening. Increase frequency.',
      nonCompliant: 'OIG checks not conducted. Billing for excluded individuals is Medicare fraud.',
      unknown: 'Review OIG check requirements.',
    },
    stateNotes: [
      'OIG LEIE check required under 42 CFR 1001',
      'Monthly screening is federal best practice',
      'Check at exclusions.oig.hhs.gov',
      'Also check SAM.gov exclusion list',
      'Billing for excluded individuals is Medicare/Medicaid fraud',
    ],
  },

  // ============================================
  // SECTION D: HEALTH & SAFETY
  // ============================================
  {
    id: 'AK-D1',
    itemId: 'D1',
    stateCode: 'AK',
    title: 'TB Screening Requirements',
    description: 'TB screening for all direct care staff',
    severity: 'high',
    regulationCode: '7 AAC 12.620',
    regulationName: 'Health Screening',
    agencyName: 'Alaska Department of Health',
    agencyAbbr: 'DOH',
    agencyPhone: '(907) 334-2483',
    agencyUrl: 'https://health.alaska.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your direct care staff have current TB screening on file?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff have current TB screening' },
          { value: 'most', label: 'Most do, some may be expired' },
          { value: 'no', label: 'No / Not tracking this' },
        ],
        required: true,
        helpText: 'TB screening must be completed before patient contact',
      },
      {
        id: 'q2',
        question: 'What type of TB screening do you accept?',
        inputType: 'select',
        options: [
          { value: 'igra', label: 'IGRA blood test (QuantiFERON or T-SPOT)' },
          { value: 'tst', label: 'Tuberculin skin test (TST)' },
          { value: 'either', label: 'Either accepted' },
          { value: 'none', label: 'No standardized screening' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'includes', questionId: 'q2', value: ['igra', 'tst', 'either'] },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'TB screening documentation is current for all direct care staff.',
      atRisk: 'Some TB screenings may need updating. Review all employee files.',
      nonCompliant: 'Staff are working without valid TB screening. Obtain testing immediately.',
      unknown: 'Verify TB screening status for all direct care staff.',
    },
    stateNotes: [
      'Alaska follows CDC guidelines for healthcare worker TB screening',
      'Baseline screening at hire is required',
      'Annual screening may be required based on risk assessment',
    ],
  },

  {
    id: 'AK-D2',
    itemId: 'D2',
    stateCode: 'AK',
    title: 'Infection Control Program',
    description: 'Comprehensive infection control program required',
    severity: 'high',
    regulationCode: '7 AAC 12.531',
    regulationName: 'Infection Control',
    agencyName: 'Alaska Department of Health',
    agencyAbbr: 'DOH',
    agencyPhone: '(907) 334-2483',
    agencyUrl: 'https://health.alaska.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have a written infection control program that includes:',
        inputType: 'checklist',
        options: [
          { value: 'policies', label: 'Written infection control policies and procedures' },
          { value: 'training', label: 'Staff training on infection control' },
          { value: 'ppe', label: 'PPE availability and proper use protocols' },
          { value: 'hand_hygiene', label: 'Hand hygiene protocols' },
          { value: 'surveillance', label: 'Infection surveillance and reporting' },
          { value: 'exposure', label: 'Exposure control plan (bloodborne pathogens)' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', questionId: 'q1', value: 6, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 4, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Infection control program meets requirements.',
      atRisk: 'Infection control program is incomplete. Update your program.',
      nonCompliant: 'Infection control program does not meet requirements.',
      unknown: 'Review infection control program requirements.',
    },
    stateNotes: [
      'Infection control required per 7 AAC 12.531',
      'Must include written policies, staff training, and surveillance',
      'Bloodborne pathogen exposure control plan required under OSHA',
      'Hand hygiene and PPE protocols essential components',
    ],
  },

  {
    id: 'AK-D3',
    itemId: 'D3',
    stateCode: 'AK',
    title: 'Emergency Preparedness - Alaska-Specific',
    description: 'Emergency preparedness with considerations for Alaska conditions',
    severity: 'high',
    regulationCode: '7 AAC 12.531',
    regulationName: 'Emergency Preparedness',
    agencyName: 'Alaska Department of Health',
    agencyAbbr: 'DOH',
    agencyPhone: '(907) 334-2483',
    agencyUrl: 'https://health.alaska.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Does your emergency preparedness plan address Alaska-specific considerations?',
        inputType: 'checklist',
        options: [
          { value: 'weather', label: 'Severe weather protocols (winter storms, extreme cold, extended darkness)' },
          { value: 'communication', label: 'Communication challenges in remote areas' },
          { value: 'evacuation', label: 'Evacuation procedures (including medevac/air transport)' },
          { value: 'supplies', label: 'Emergency supply provisions for extended isolation' },
          { value: 'staff_safety', label: 'Staff safety during travel to remote patients' },
          { value: 'earthquake', label: 'Earthquake and tsunami preparedness' },
          { value: 'volcano', label: 'Volcanic activity awareness (where applicable)' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'When was your emergency plan last reviewed and tested?',
        inputType: 'select',
        options: [
          { value: 'within_year', label: 'Within the past year' },
          { value: '1_2_years', label: '1-2 years ago' },
          { value: 'over_2', label: 'Over 2 years ago' },
          { value: 'never', label: 'Never tested' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 5 },
        { condition: 'equals', questionId: 'q2', value: 'within_year' },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Emergency preparedness plan addresses Alaska-specific considerations.',
      atRisk: 'Emergency plan may not adequately address Alaska-specific challenges.',
      nonCompliant: 'Emergency preparedness plan does not meet Alaska requirements.',
      unknown: 'Review emergency preparedness plan.',
    },
    stateNotes: [
      'Alaska experiences unique natural hazards including earthquakes, volcanic activity, and extreme weather',
      'Many communities have limited road access - plan for air evacuation',
      'Winter conditions can isolate communities for extended periods',
      'Communication infrastructure may be limited in remote areas',
    ],
  },

  {
    id: 'AK-D4',
    itemId: 'D4',
    stateCode: 'AK',
    title: 'Cold Weather Safety Protocols',
    description: 'Alaska-specific cold weather safety for staff and patients',
    severity: 'medium',
    regulationCode: '7 AAC 12.531',
    regulationName: 'Safety',
    agencyName: 'Alaska Department of Health',
    agencyAbbr: 'DOH',
    agencyPhone: '(907) 334-2483',
    agencyUrl: 'https://health.alaska.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have cold weather safety protocols?',
        inputType: 'checklist',
        options: [
          { value: 'travel', label: 'Safe travel guidelines (when to cancel visits)' },
          { value: 'vehicle', label: 'Vehicle emergency kits and survival gear' },
          { value: 'communication', label: 'Check-in procedures for remote visits' },
          { value: 'cancellation', label: 'Visit cancellation criteria (temperature, wind chill)' },
          { value: 'frostbite', label: 'Frostbite/hypothermia recognition' },
          { value: 'darkness', label: 'Extended darkness safety measures' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', questionId: 'q1', value: 5, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Cold weather safety protocols are adequate for Alaska operations.',
      atRisk: 'Enhance cold weather safety procedures.',
      nonCompliant: 'Cold weather protocols are insufficient for Alaska conditions.',
      unknown: 'Review cold weather safety requirements.',
    },
    stateNotes: [
      'Alaska experiences extreme cold and extended darkness in winter',
      'Vehicle emergency kits should include survival gear for cold weather',
      'Establish clear cancellation criteria based on temperature and conditions',
      'Staff check-in procedures critical for remote visits',
      'Frostbite and hypothermia recognition training recommended',
    ],
  },

  {
    id: 'AK-D5',
    itemId: 'D5',
    stateCode: 'AK',
    title: 'PPE and Safety Equipment',
    description: 'Personal protective equipment availability',
    severity: 'high',
    regulationCode: '29 CFR 1910',
    regulationName: 'PPE',
    agencyName: 'Alaska Occupational Safety and Health',
    agencyAbbr: 'AKOSH',
    agencyPhone: '(907) 269-4940',
    agencyUrl: 'https://labor.alaska.gov/lss/oshhome.htm',
    questions: [
      {
        id: 'q1',
        question: 'Do you maintain adequate PPE supplies?',
        inputType: 'checklist',
        options: [
          { value: 'gloves', label: 'Disposable gloves' },
          { value: 'masks', label: 'Face masks/N95 respirators' },
          { value: 'gowns', label: 'Isolation gowns' },
          { value: 'eyewear', label: 'Eye protection' },
          { value: 'sanitizer', label: 'Hand sanitizer' },
          { value: 'bags', label: 'Biohazard bags' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Are staff trained on proper PPE use?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 5 },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'PPE supplies and training are adequate.',
      atRisk: 'Some PPE supplies or training may be missing.',
      nonCompliant: 'Inadequate PPE supplies or training.',
      unknown: 'Review PPE requirements.',
    },
    stateNotes: [
      'AKOSH (Alaska Occupational Safety and Health) enforces PPE requirements',
      'Contact AKOSH at (907) 269-4940',
      'Employers must provide PPE at no cost to employees',
      'Training on proper PPE use and disposal required',
      '29 CFR 1910 standards apply through state OSHA plan',
    ],
  },

  // ============================================
  // SECTION E: TRAINING & COMPETENCY
  // ============================================
  {
    id: 'AK-E1',
    itemId: 'E1',
    stateCode: 'AK',
    title: 'Orientation Training',
    description: 'Comprehensive orientation for all new employees',
    severity: 'high',
    regulationCode: '7 AAC 12.519',
    regulationName: 'Orientation',
    agencyName: 'Alaska Department of Health',
    agencyAbbr: 'DOH',
    agencyPhone: '(907) 334-2483',
    agencyUrl: 'https://health.alaska.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL new employees complete orientation training BEFORE patient contact that covers:',
        inputType: 'checklist',
        options: [
          { value: 'agency_policies', label: 'Agency policies and procedures' },
          { value: 'patient_rights', label: 'Patient rights (7 AAC 12.534)' },
          { value: 'safety', label: 'Safety procedures including emergency protocols' },
          { value: 'infection', label: 'Infection control practices' },
          { value: 'abuse', label: 'Abuse and neglect reporting (7 AAC 12.541)' },
          { value: 'documentation', label: 'Documentation requirements' },
          { value: 'hipaa', label: 'HIPAA privacy and confidentiality' },
          { value: 'cultural', label: 'Cultural competency' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', questionId: 'q1', value: 7, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 5, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 5, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Orientation training program meets Alaska requirements.',
      atRisk: 'Orientation training may be incomplete. Review training content.',
      nonCompliant: 'Staff are providing care without required orientation training.',
      unknown: 'Review orientation training program.',
    },
    stateNotes: [
      'Orientation must be completed before patient contact per 7 AAC 12.519',
      'Must include patient rights (7 AAC 12.534) and abuse reporting (7 AAC 12.541)',
      'Document completion in personnel file',
      'Cultural competency training recommended for Alaska populations',
    ],
  },

  {
    id: 'AK-E2',
    itemId: 'E2',
    stateCode: 'AK',
    title: 'Home Health Aide Training (7 AAC 12.519)',
    description: 'Alaska requires 75 hours of training for home health aides',
    severity: 'high',
    regulationCode: '7 AAC 12.519',
    regulationName: 'Home Health Aide',
    agencyName: 'Alaska Department of Health',
    agencyAbbr: 'DOH',
    agencyPhone: '(907) 334-2483',
    agencyUrl: 'https://health.alaska.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your home health aides have at least 75 hours of training documented (including 16 hours clinical)?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all HHAs have 75+ hours documented' },
          { value: 'most', label: 'Most have it, some are in progress' },
          { value: 'no', label: 'No / Not tracking training hours' },
        ],
        required: true,
        helpText: 'Per 7 AAC 12.519, HHAs must complete 75 hours training including 16 hours supervised clinical.',
      },
      {
        id: 'q2',
        question: 'Are home health aides certified as nurse aides in Alaska?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all are certified' },
          { value: 'some', label: 'Some are certified' },
          { value: 'no', label: 'No' },
        ],
      },
      {
        id: 'q3',
        question: 'Has training included both classroom instruction and supervised practical training?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No / Not sure' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      // Nurse aide certification status per 7 AAC 12.519
      { condition: 'equals', questionId: 'q2', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'some', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Home health aide training meets 75-hour requirement per 7 AAC 12.519.',
      atRisk: 'Some home health aides may not have completed 75 hours, training components are incomplete, or not all are certified as nurse aides.',
      nonCompliant: 'Home health aides are working without required 75 hours of training. This violates 7 AAC 12.519.',
      unknown: 'Review home health aide training documentation.',
    },
    stateNotes: [
      'Alaska requires 75 hours of training (federal minimum)',
      '16 hours must be supervised clinical training',
      'HHAs working for Medicare-certified agencies must also be certified as nurse aides',
      'Competency evaluation required covering 12+ skill areas',
    ],
  },

  {
    id: 'AK-E3',
    itemId: 'E3',
    stateCode: 'AK',
    title: 'Annual In-Service Training (12 Hours)',
    description: 'Alaska requires 12 hours of annual in-service training for HHAs',
    severity: 'medium',
    regulationCode: '7 AAC 12.519',
    regulationName: 'Annual Training',
    agencyName: 'Alaska Department of Health',
    agencyAbbr: 'DOH',
    agencyPhone: '(907) 334-2483',
    agencyUrl: 'https://health.alaska.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL home health aides complete 12 hours of in-service training annually?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all HHAs complete 12+ hours annually' },
          { value: 'most', label: 'Most complete training, some are behind' },
          { value: 'no', label: 'No structured annual training program' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Is in-service training documented in personnel files?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Annual in-service training meets 12-hour requirement per 7 AAC 12.519.',
      atRisk: 'Some home health aides may be behind on annual training.',
      nonCompliant: 'No structured annual in-service training program exists.',
      unknown: 'Review annual in-service training program.',
    },
    stateNotes: [
      '12 hours annual in-service required per 7 AAC 12.519',
      'Training must address agency-specific needs and patient population',
      'Document all training in personnel files with dates and topics',
      'Track training hours to ensure compliance by anniversary date',
    ],
  },

  {
    id: 'AK-E4',
    itemId: 'E4',
    stateCode: 'AK',
    title: 'Competency Evaluation',
    description: 'Skills competency assessment for all clinical staff',
    severity: 'high',
    regulationCode: '7 AAC 12.519',
    regulationName: 'Competency',
    agencyName: 'Alaska Department of Health',
    agencyAbbr: 'DOH',
    agencyPhone: '(907) 334-2483',
    agencyUrl: 'https://health.alaska.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you conduct competency evaluations for home health aides covering all required skills?',
        inputType: 'checklist',
        options: [
          { value: 'communication', label: 'Communication and reporting' },
          { value: 'infection', label: 'Infection control' },
          { value: 'vitals', label: 'Vital signs' },
          { value: 'hygiene', label: 'Personal hygiene assistance' },
          { value: 'nutrition', label: 'Nutrition and fluids' },
          { value: 'ambulation', label: 'Ambulation and transfer' },
          { value: 'positioning', label: 'Positioning and range of motion' },
          { value: 'emergency', label: 'Emergency procedures' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'How often are competencies re-evaluated?',
        inputType: 'select',
        options: [
          { value: 'annually', label: 'Annually' },
          { value: 'biannually', label: 'Every 2 years' },
          { value: 'hire_only', label: 'Only at hire' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 7 },
        { condition: 'equals', questionId: 'q2', value: 'annually' },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 5, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 5, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Competency evaluations meet 7 AAC 12.519 requirements.',
      atRisk: 'Competency evaluations may be incomplete.',
      nonCompliant: 'Required competency evaluations not conducted.',
      unknown: 'Review competency requirements.',
    },
    stateNotes: [
      'Competency evaluation required per 7 AAC 12.519',
      'Must cover at least 12 skill areas specified in federal requirements',
      'Annual re-evaluation recommended',
      'RN must conduct or supervise competency evaluations',
      'Document results in personnel file',
    ],
  },

  {
    id: 'AK-E5',
    itemId: 'E5',
    stateCode: 'AK',
    title: 'Mandatory Abuse Reporting (AS 47.24.010)',
    description: 'Alaska requires healthcare workers to report suspected abuse of vulnerable adults per AS 47.24.010',
    severity: 'high',
    regulationCode: 'AS 47.24.010',
    regulationName: 'Mandated Reporting',
    agencyName: 'Alaska Adult Protective Services',
    agencyAbbr: 'APS',
    agencyPhone: '(907) 269-3666',
    agencyUrl: 'https://health.alaska.gov/en/services/aps-report-harm/',
    questions: [
      {
        id: 'q1',
        question: 'Do all staff receive training on recognizing and reporting abuse/neglect?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Are staff aware of Alaska mandatory reporting requirements?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
      {
        id: 'q3',
        question: 'Do staff know how to report abuse to appropriate Alaska authorities?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'no' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All staff are trained on mandatory abuse reporting per AS 47.24.010.',
      atRisk: 'Review mandatory reporting procedures with all staff. Reports must be made within 24 hours.',
      nonCompliant: 'Staff are not trained on mandatory abuse reporting. This violates AS 47.24.010. Failure to report is a Class B misdemeanor.',
      unknown: 'Review abuse reporting requirements under AS 47.24.010.',
    },
    stateNotes: [
      'Alaska APS Hotline: 1-800-478-9996 (in-state) or (907) 269-3666',
      'Home health employees are mandatory reporters under AS 47.24.010',
      'Reports must be made within 24 hours of first having cause for belief',
      'Failure to report is a Class B misdemeanor per AS 47.24.010',
      'Report online at health.alaska.gov/en/services/aps-report-harm/',
    ],
  },

  {
    id: 'AK-E6',
    itemId: 'E6',
    stateCode: 'AK',
    title: 'Cultural Competency Training',
    description: 'Alaska Native and diverse population cultural competency',
    severity: 'medium',
    regulationCode: '7 AAC 12.531',
    regulationName: 'Cultural Competency',
    agencyName: 'Alaska Department of Health',
    agencyAbbr: 'DOH',
    agencyPhone: '(907) 334-2483',
    agencyUrl: 'https://health.alaska.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do staff receive cultural competency training relevant to Alaska populations?',
        inputType: 'select',
        options: [
          { value: 'comprehensive', label: 'Yes, comprehensive training including Alaska Native cultures' },
          { value: 'basic', label: 'Basic orientation only' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Does training cover: (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'an_cultures', label: 'Alaska Native cultures and traditions' },
          { value: 'communication', label: 'Cross-cultural communication' },
          { value: 'health_beliefs', label: 'Traditional health beliefs and practices' },
          { value: 'language', label: 'Language considerations' },
          { value: 'rural', label: 'Rural and subsistence lifestyle awareness' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'includes',
          value: ['comprehensive', 'basic'],
        },
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'comprehensive' },
        { condition: 'countAtLeast', questionId: 'q2', value: 4 },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'basic', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Cultural competency training is comprehensive and appropriate for Alaska.',
      atRisk: 'Consider enhancing cultural competency training, especially regarding Alaska Native populations.',
      nonCompliant: 'No cultural competency training. This is essential for serving Alaska\'s diverse populations.',
      unknown: 'Review cultural training needs.',
    },
    stateNotes: [
      'Alaska has 229 federally recognized tribes',
      'Many patients may have traditional health beliefs that should be respected',
      'Language interpreters may be needed in some communities',
    ],
  },

  {
    id: 'AK-E7',
    itemId: 'E7',
    stateCode: 'AK',
    title: 'CPR and First Aid Certification',
    description: 'Emergency response certification for direct care staff',
    severity: 'high',
    regulationCode: '7 AAC 12.519',
    regulationName: 'Emergency Training',
    agencyName: 'Alaska Department of Health',
    agencyAbbr: 'DOH',
    agencyPhone: '(907) 334-2483',
    agencyUrl: 'https://health.alaska.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do all direct care staff maintain current CPR certification?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff have current CPR' },
          { value: 'most', label: 'Most staff, some may be expired' },
          { value: 'no', label: 'No / Not required' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Are certifications from approved providers (AHA, Red Cross)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No / Not sure' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'CPR certifications are current for all direct care staff.',
      atRisk: 'Some CPR certifications may be expired. Schedule recertification.',
      nonCompliant: 'Staff lacking CPR certification.',
      unknown: 'Review CPR certification requirements.',
    },
    stateNotes: [
      'CPR certification recommended for all direct care staff',
      'Accepted providers: AHA, Red Cross, or equivalent',
      'Certification typically valid for 2 years',
      'Track expiration dates to ensure timely renewal',
    ],
  },

  // ============================================
  // SECTION F: CARE DELIVERY
  // ============================================
  {
    id: 'AK-F1',
    itemId: 'F1',
    stateCode: 'AK',
    title: 'Plan of Care Requirements (7 AAC 12.513)',
    description: 'Alaska requires individualized plans of care for each patient',
    severity: 'high',
    regulationCode: '7 AAC 12.513',
    regulationName: 'Plan of Care',
    agencyName: 'Alaska Department of Health',
    agencyAbbr: 'DOH',
    agencyPhone: '(907) 334-2483',
    agencyUrl: 'https://health.alaska.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a written plan of care for EVERY patient before services begin?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always before first visit' },
          { value: 'usually', label: 'Usually, but sometimes services start first' },
          { value: 'no', label: 'No formal care plan process' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do your plans of care include all required elements per 7 AAC 12.513?',
        inputType: 'checklist',
        options: [
          { value: 'diagnosis', label: 'Diagnosis and prognosis' },
          { value: 'services', label: 'Types and frequency of services' },
          { value: 'goals', label: 'Patient goals and expected outcomes' },
          { value: 'physician', label: 'Physician/practitioner orders and signature' },
          { value: 'review', label: 'Schedule for plan review (at least every 60 days)' },
          { value: 'disciplines', label: 'All disciplines involved in care' },
          { value: 'medications', label: 'Medications and treatments' },
        ],
      },
      {
        id: 'q3',
        question: 'Are care plans reviewed at least every 60 days?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'countAtLeast', questionId: 'q2', value: 6 },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'usually', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Plans of care meet 7 AAC 12.513 requirements.',
      atRisk: 'Plans of care may be incomplete or created after services begin.',
      nonCompliant: 'Services are being provided without written plans of care. This violates 7 AAC 12.513.',
      unknown: 'Review plan of care process against 7 AAC 12.513 requirements.',
    },
    stateNotes: [
      'Care plan required before services begin per 7 AAC 12.513',
      'Must be reviewed and updated at least every 60 days',
      'Physician/practitioner signature required',
      'Must include diagnoses, services, frequency, goals, and medications',
    ],
  },

  {
    id: 'AK-F2',
    itemId: 'F2',
    stateCode: 'AK',
    title: 'Nursing Services (7 AAC 12.517)',
    description: 'RN supervision and nursing service requirements',
    severity: 'high',
    regulationCode: '7 AAC 12.517',
    regulationName: 'Nursing Services',
    agencyName: 'Alaska Department of Health',
    agencyAbbr: 'DOH',
    agencyPhone: '(907) 334-2483',
    agencyUrl: 'https://health.alaska.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Are nursing services provided and supervised by a registered nurse?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Are home health aides supervised by an RN per requirements?',
        inputType: 'select',
        options: [
          { value: 'per_regs', label: 'Yes, supervisory visits every 14 days for skilled patients' },
          { value: 'less_frequent', label: 'Yes, but less frequently' },
          { value: 'no', label: 'No formal RN supervision of aides' },
        ],
      },
      {
        id: 'q3',
        question: 'For remote patients, are alternative supervision methods documented?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes, with documented rationale' },
          { value: 'partial', label: 'Partially documented' },
          { value: 'na', label: 'Not applicable - no remote patients' },
          { value: 'no', label: 'No documentation' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'per_regs' },
        { condition: 'includes', questionId: 'q3', value: ['yes', 'na'] },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'less_frequent' },
        { condition: 'equals', questionId: 'q3', value: 'partial' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Nursing services meet 7 AAC 12.517 requirements.',
      atRisk: 'Supervision frequency or documentation may not meet requirements.',
      nonCompliant: 'Nursing service requirements not met per 7 AAC 12.517.',
      unknown: 'Review nursing service requirements.',
    },
    stateNotes: [
      'RN supervision required per 7 AAC 12.517',
      'Skilled patients: HHA supervision every 14 days',
      'Non-skilled patients: supervision every 60 days',
      'Remote patient supervision may require alternative methods with documentation',
    ],
  },

  {
    id: 'AK-F3',
    itemId: 'F3',
    stateCode: 'AK',
    title: 'Patient Rights (7 AAC 12.534)',
    description: 'Patient rights notification and compliance',
    severity: 'high',
    regulationCode: '7 AAC 12.534',
    regulationName: 'Patient Rights',
    agencyName: 'Alaska Department of Health',
    agencyAbbr: 'DOH',
    agencyPhone: '(907) 334-2483',
    agencyUrl: 'https://health.alaska.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Are patients informed of their rights at admission per 7 AAC 12.534?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do patients receive written notice including: (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'rights', label: 'Statement of patient rights' },
          { value: 'services', label: 'Services to be provided' },
          { value: 'charges', label: 'Charges and payment policies' },
          { value: 'complaint', label: 'Complaint procedures' },
          { value: 'hotline', label: 'State complaint hotline information' },
        ],
      },
      {
        id: 'q3',
        question: 'Do patients sign acknowledgment of receiving rights information?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Patient rights notification meets 7 AAC 12.534 requirements.',
      atRisk: 'Obtain signed acknowledgments and ensure all required information is provided.',
      nonCompliant: 'Patient rights not communicated per 7 AAC 12.534.',
      unknown: 'Review patient rights requirements.',
    },
    stateNotes: [
      'Patient rights notification required at admission per 7 AAC 12.534',
      'Must include services, charges, complaint procedures',
      'State complaint hotline must be provided',
      'Signed acknowledgment should be in patient record',
    ],
  },

  {
    id: 'AK-F4',
    itemId: 'F4',
    stateCode: 'AK',
    title: 'Advance Directives (7 AAC 12.537)',
    description: 'Documentation and honoring of patient advance directives',
    severity: 'medium',
    regulationCode: '7 AAC 12.537',
    regulationName: 'Advance Directives',
    agencyName: 'Alaska Department of Health',
    agencyAbbr: 'DOH',
    agencyPhone: '(907) 334-2483',
    agencyUrl: 'https://health.alaska.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you document whether patients have advance directives?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you provide information about advance directives to patients?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Advance directive procedures meet 7 AAC 12.537 requirements.',
      atRisk: 'Provide advance directive information to patients.',
      nonCompliant: 'Not documenting advance directives per 7 AAC 12.537.',
      unknown: 'Review advance directive requirements.',
    },
    stateNotes: [
      'Advance directive documentation required per 7 AAC 12.537',
      'Must ask patients if they have advance directives',
      'Provide information about advance directives upon request',
      'Document patient wishes in clinical record',
    ],
  },

  {
    id: 'AK-F5',
    itemId: 'F5',
    stateCode: 'AK',
    title: 'Patient Acceptance (7 AAC 12.514)',
    description: 'Patient admission and acceptance procedures',
    severity: 'medium',
    regulationCode: '7 AAC 12.514',
    regulationName: 'Acceptance',
    agencyName: 'Alaska Department of Health',
    agencyAbbr: 'DOH',
    agencyPhone: '(907) 334-2483',
    agencyUrl: 'https://health.alaska.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have documented patient acceptance criteria per 7 AAC 12.514?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you assess whether services can be safely provided before accepting patients?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Patient acceptance procedures meet 7 AAC 12.514 requirements.',
      atRisk: 'Ensure pre-acceptance assessment of ability to provide services.',
      nonCompliant: 'No documented patient acceptance criteria.',
      unknown: 'Review patient acceptance requirements.',
    },
    stateNotes: [
      'Patient acceptance criteria required per 7 AAC 12.514',
      'Must assess ability to safely provide ordered services',
      'Consider geographic accessibility for Alaska locations',
      'Document acceptance criteria and admission decisions',
    ],
  },

  // ============================================
  // SECTION G: DOCUMENTATION/ADMIN
  // ============================================
  {
    id: 'AK-G1',
    itemId: 'G1',
    stateCode: 'AK',
    title: 'Clinical Record Requirements (7 AAC 12.890)',
    description: 'Clinical record documentation requirements',
    severity: 'high',
    regulationCode: '7 AAC 12.890',
    regulationName: 'Clinical Records',
    agencyName: 'Alaska Department of Health',
    agencyAbbr: 'DOH',
    agencyPhone: '(907) 334-2483',
    agencyUrl: 'https://health.alaska.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do patient clinical records include all required elements?',
        inputType: 'checklist',
        options: [
          { value: 'patient_info', label: 'Patient identification and demographic information' },
          { value: 'physician_orders', label: 'Physician/practitioner orders and authorizations' },
          { value: 'care_plan', label: 'Plan of care' },
          { value: 'visit_notes', label: 'Documentation of each visit/service' },
          { value: 'assessments', label: 'Clinical assessments and evaluations' },
          { value: 'medications', label: 'Medication list and administration records' },
          { value: 'progress', label: 'Progress notes' },
          { value: 'discharge', label: 'Discharge summary' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Are clinical records completed timely after each visit?',
        inputType: 'select',
        options: [
          { value: 'same_day', label: 'Yes, same day or within 24 hours' },
          { value: 'within_week', label: 'Within a week' },
          { value: 'delayed', label: 'Often delayed beyond a week' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 7 },
        { condition: 'equals', questionId: 'q2', value: 'same_day' },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 5, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 5, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Clinical records contain all required elements and are completed timely.',
      atRisk: 'Clinical records may be missing some elements or documentation is delayed.',
      nonCompliant: 'Clinical records do not meet requirements.',
      unknown: 'Review clinical record requirements.',
    },
    stateNotes: [
      'Clinical record requirements per 7 AAC 12.890',
      'Documentation should be completed within 24 hours of service',
      'Must include all visits, assessments, and changes in condition',
      'Electronic records must meet security and backup requirements',
    ],
  },

  {
    id: 'AK-G2',
    itemId: 'G2',
    stateCode: 'AK',
    title: 'Record Retention (7 AAC 12.910)',
    description: 'Record retention requirements',
    severity: 'medium',
    regulationCode: '7 AAC 12.910',
    regulationName: 'Record Retention',
    agencyName: 'Alaska Department of Health',
    agencyAbbr: 'DOH',
    agencyPhone: '(907) 334-2483',
    agencyUrl: 'https://health.alaska.gov/',
    questions: [
      {
        id: 'q1',
        question: 'How long do you retain patient and employee records after discharge/termination?',
        inputType: 'select',
        options: [
          { value: '6plus', label: '6 years or longer' },
          { value: '5', label: '5 years' },
          { value: '3to4', label: '3-4 years' },
          { value: 'less3', label: 'Less than 3 years' },
          { value: 'unsure', label: 'Not sure / no formal policy' },
        ],
        required: true,
        helpText: 'Alaska and Medicare require minimum 5-6 years retention.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: '6plus', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: '5', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: '3to4', result: 'AT_RISK' },
      { condition: 'includes', questionId: 'q1', value: ['less3', 'unsure'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your record retention policy meets Alaska requirements.',
      atRisk: 'Consider extending retention period to at least 5-6 years.',
      nonCompliant: 'Your record retention does not meet requirements. Update policy immediately.',
      unknown: 'Review record retention requirements.',
    },
    stateNotes: [
      'Record retention per 7 AAC 12.910',
      'Minimum 5-6 years from discharge recommended',
      'Medicare requires 5 years from cost report filing',
      'Consider longer retention for minors (until age of majority + statute of limitations)',
    ],
  },

  {
    id: 'AK-G3',
    itemId: 'G3',
    stateCode: 'AK',
    title: 'HIPAA Compliance',
    description: 'Protected health information safeguards',
    severity: 'high',
    regulationCode: '45 CFR 164',
    regulationName: 'HIPAA',
    agencyName: 'U.S. Department of Health and Human Services - Office for Civil Rights',
    agencyAbbr: 'HHS-OCR',
    agencyPhone: '(800) 368-1019',
    agencyUrl: 'https://www.hhs.gov/hipaa/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have written HIPAA policies and procedures?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do all staff complete HIPAA training?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
      {
        id: 'q3',
        question: 'Do you have a designated Privacy Officer?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'no' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'HIPAA compliance program is complete.',
      atRisk: 'HIPAA program needs enhancement. Complete staff training and designate Privacy Officer.',
      nonCompliant: 'No HIPAA policies. This violates federal law.',
      unknown: 'Review HIPAA requirements.',
    },
    stateNotes: [
      'HIPAA compliance required under 45 CFR 164',
      'Privacy Officer designation required',
      'Annual staff training recommended',
      'Business Associate Agreements required with vendors',
      'Breach notification procedures must be documented',
    ],
  },

  {
    id: 'AK-G4',
    itemId: 'G4',
    stateCode: 'AK',
    title: 'Complaint and Grievance Procedures',
    description: 'Patient complaint handling documentation',
    severity: 'medium',
    regulationCode: '7 AAC 12.531',
    regulationName: 'Complaints',
    agencyName: 'Alaska Department of Health',
    agencyAbbr: 'DOH',
    agencyPhone: '(907) 334-2483',
    agencyUrl: 'https://health.alaska.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a formal complaint/grievance procedure?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Are patients informed of complaint procedures and state hotline?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
      {
        id: 'q3',
        question: 'Are complaints documented, investigated, and resolved?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'no' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Complaint procedures are documented and patients are informed.',
      atRisk: 'Ensure patients know how to file complaints and all complaints are documented.',
      nonCompliant: 'No complaint procedures.',
      unknown: 'Review complaint procedure requirements.',
    },
    stateNotes: [
      'Alaska Health Facility Licensing complaint line: (907) 334-2483',
      'After hours: 1-888-387-9387',
    ],
  },

  {
    id: 'AK-G5',
    itemId: 'G5',
    stateCode: 'AK',
    title: 'Billing and Documentation Accuracy',
    description: 'Accurate billing documentation requirements',
    severity: 'high',
    regulationCode: '42 CFR 424',
    regulationName: 'Billing',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do clinical notes support all billed services?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, documentation always supports billing' },
          { value: 'usually', label: 'Usually' },
          { value: 'inconsistent', label: 'Inconsistent' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you conduct periodic billing audits?',
        inputType: 'select',
        options: [
          { value: 'quarterly', label: 'Yes, at least quarterly' },
          { value: 'annually', label: 'Annually' },
          { value: 'never', label: 'No audits' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'includes', questionId: 'q2', value: ['quarterly', 'annually'] },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'usually', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'inconsistent', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Billing documentation is accurate and audited regularly.',
      atRisk: 'Improve documentation consistency and implement regular audits.',
      nonCompliant: 'Documentation does not support billing. This creates fraud risk.',
      unknown: 'Review billing documentation requirements.',
    },
    stateNotes: [
      'Billing documentation requirements under 42 CFR 424',
      'Clinical notes must support all billed services',
      'Quarterly billing audits recommended',
      'False Claims Act liability for inaccurate billing',
      'Medicare RAC and ZPIC audits may review claims',
    ],
  },
];

export default ALASKA_QUESTIONS;